import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PROFESSION_CATEGORIES, LANGUAGES, API_BASE_URL, API_URL } from './constants';
import ArticleFeedbackForm from './components/ArticleFeedbackForm';

// These components will receive props from the parent App component
export const Articles = ({ 
  articles, 
  selectedCategory, 
  setSelectedCategory, 
  selectedLanguage, 
  setSelectedLanguage, 
  selectedSource,
  setSelectedSource,
  selectedSort,
  setSelectedSort,
  completionStatuses, 
  handleReadArticle, 
  startTest, 
  testLoading,
  loading,
  LANGUAGES 
}) => {
  const { t } = useTranslation();
  // Content mode: 'articles' | 'youtube'
  const [contentMode, setContentMode] = useState('articles');

  // Helper to detect YouTube links
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    try {
      const u = new URL(url);
      const host = u.hostname.replace('www.', '');
      return host === 'youtube.com' || host === 'youtu.be' || u.pathname.includes('/embed/');
    } catch { return false; }
  };

  // Get unique categories from current mode
  const ytPredicate = (a) => isYouTubeUrl(a.external_url);
  const categories = ['All', ...new Set(
    articles
      .filter(a => contentMode === 'youtube' ? ytPredicate(a) : !ytPredicate(a))
      .flatMap(a => [a.category, a.secondary_category].filter(Boolean))
  )];
  
  // Get unique languages from articles and add "All" option
  const languages = ['All', ...LANGUAGES];
  
  // Get unique sources from articles and add "All" option
  const normalizeSource = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const sourceMap = articles
    .filter(article => article.source)
    .reduce((acc, article) => {
      const key = normalizeSource(article.source);
      if (key && !acc[key]) acc[key] = article.source.trim();
      return acc;
    }, {});
  const sources = ['All', ...Object.entries(sourceMap)
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([key, label]) => ({ key, label }))
  ];
  
  // Sort options
  const sortOptions = [
    { value: 'newest', label: t('sortNewest', 'Newest') },
    { value: 'oldest', label: t('sortOldest', 'Oldest') },
    { value: 'title', label: t('sortTitleAZ', 'Title A-Z') },
    { value: 'points', label: t('sortPePoints', 'PE Points') }
  ];
  
  

  // Filter and sort content depending on mode
  const filteredAndSortedArticles = articles
    .filter(article => {
      const categoryMatch = selectedCategory === 'All' || article.category === selectedCategory || article.secondary_category === selectedCategory;
      const languageMatch = selectedLanguage === 'All' || article.language === selectedLanguage;
      const sourceMatch = selectedSource === 'All' || normalizeSource(article.source) === selectedSource;
      const ytMatch = contentMode === 'youtube' ? isYouTubeUrl(article.external_url) : !isYouTubeUrl(article.external_url);
      return categoryMatch && languageMatch && (contentMode === 'articles' ? sourceMatch : true) && ytMatch;
    })
    .sort((a, b) => {
      switch (selectedSort) {
        case 'newest':
          return new Date(b.publication_date || b.created_at) - new Date(a.publication_date || a.created_at);
        case 'oldest':
          return new Date(a.publication_date || a.created_at) - new Date(b.publication_date || b.created_at);
        case 'title':
          return a.title.localeCompare(b.title);
        case 'points':
          return b.pe_points_value - a.pe_points_value;
        default:
          return 0;
      }
    });

  return (
    <div className="articles-page-container">
      {/* Header */}
      <div className="articles-header">
        <h1>{contentMode === 'youtube' ? t('youtubeLibrary', 'YouTube Library') : t('articlesLibrary', 'Articles Library')}</h1>
        <p>{contentMode === 'youtube' ? t('youtubeSubtitle', 'Watch and learn from curated YouTube videos.') : t('articlesSubtitle', 'Browse our curated library of professional development articles.')}</p>
        <div className="content-switch">
          <button className={`switch-btn ${contentMode === 'youtube' ? 'active' : ''}`} onClick={() => { setContentMode('youtube'); }}>{t('youtubeVideos', 'YouTube Videos')}</button>
          <button className={`switch-btn ${contentMode === 'articles' ? 'active' : ''}`} onClick={() => { setContentMode('articles'); }}>{t('articles', 'Articles')}</button>
        </div>
      </div>

      {/* Filters */}
      <div className="card filters-card">
        <div className="filter-group">
          <label>{t('category', 'Category')}</label>
          <div className="category-filters">
            {categories.map(category => (
              <button
                key={category}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
          
        </div>
        <div className="filter-group">
          <label>{t('language', 'Language')}</label>
          <div className="language-filters">
            {languages.map(lang => (
              <button
                key={lang}
                className={`language-btn ${selectedLanguage === lang ? 'active' : ''}`}
                onClick={() => setSelectedLanguage(lang)}
              >
                {lang}
              </button>
            ))}
          </div>
        </div>
        {contentMode === 'articles' && (
          <div className="filter-group">
            <label>{t('source', 'Source')}</label>
            <div className="source-filters">
              <button
                key="All"
                className={`source-btn ${selectedSource === 'All' ? 'active' : ''}`}
                onClick={() => setSelectedSource('All')}
              >
                {t('all', 'All')}
              </button>
              {sources.filter(s => s !== 'All').map(({ key, label }) => (
                <button
                  key={key}
                  className={`source-btn ${selectedSource === key ? 'active' : ''}`}
                  onClick={() => setSelectedSource(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="filter-group">
          <label>{t('sortBy', 'Sort By')}</label>
          <div className="sort-filters">
            {sortOptions.map(option => (
              <button
                key={option.value}
                className={`sort-btn ${selectedSort === option.value ? 'active' : ''}`}
                onClick={() => setSelectedSort(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="articles-grid">
        {loading ? (
          <div className="loading-message">
            <p>{contentMode === 'youtube' ? t('loadingVideos', 'Loading videos...') : t('loadingArticles', 'Loading articles...')}</p>
          </div>
        ) : filteredAndSortedArticles.length > 0 ? (
          filteredAndSortedArticles.map(article => {
            const isCompleted = completionStatuses[article.id]?.completed;
            const canTakeTest = completionStatuses[article.id]?.can_take_test;

            return (
              <div key={article.id} className={`article-card card ${isCompleted ? 'completed' : ''}`}>
                <div className="article-card-header">
                  <h3>{article.title}</h3>
                  <div className="header-right">
                    {/* Show YouTube logo for YouTube external links */}
                    {(() => {
                      const url = article.external_url;
                      let isYT = false;
                      try {
                        if (url) {
                          const u = new URL(url);
                          const host = u.hostname.replace('www.', '');
                          isYT = (host === 'youtube.com' || host === 'youtu.be') || u.pathname.includes('/embed/');
                        }
                      } catch {}
                      return isYT ? (
                        <img src="/YouTube-Logo-PNG-Symbol-for-Video-Platform-Transparent-jpg.webp" alt="YouTube" style={{ height: 18, marginRight: 8 }} />
                      ) : null;
                    })()}
                    <span className="pe-points">{article.pe_points_value} {parseFloat(article.pe_points_value) === 1 ? t('pePoint', 'PE Point') : t('pePoints', 'PE Points')}</span>
                  </div>
                </div>
                <div className="article-tags">
                  <span className="tag">{article.category}</span>
                  {article.secondary_category && article.secondary_category !== article.category && (
                    <span className="tag">{article.secondary_category}</span>
                  )}
                  {article.subcategory && <span className="tag">{article.subcategory}</span>}
                  <span className="tag">{article.language}</span>
                  <span className="tag">{article.difficulty_level}</span>
                </div>
                {(article.source || article.publication_date) && (
                  <div className="article-meta-info">
                    {article.source && <span className="tag">{t('source', 'Source')}: {article.source}</span>}
                    {article.publication_date && (
                      <span className="tag">
                        {t('published', 'Published')}: {new Date(article.publication_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}
                <p className="article-summary">
                  {article.summary}
                </p>
                <div className="article-card-footer">
                  <button
                    className="btn-read-article"
                    onClick={() => handleReadArticle(article)}
                  >
                    {isCompleted
                      ? (isYouTubeUrl(article.external_url)
                          ? t('reviewVideo', 'Review Video')
                          : t('reviewArticle', 'Review Article'))
                      : (isYouTubeUrl(article.external_url)
                          ? t('startViewing', 'Start Viewing')
                          : t('startReading', 'Start Reading'))}
                  </button>
                  {isCompleted && (
                    <span className="completed-check">✓ {t('completed', 'Completed')}</span>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-articles card">
            <h3>{contentMode === 'youtube' ? t('noVideosFound', 'No videos found') : t('noArticlesFound', 'No articles found')}</h3>
            <p>{contentMode === 'articles' ? t('tryAdjustingFiltersArticles', 'Try adjusting your category, language, or source filters.') : t('tryAdjustingFilters', 'Try adjusting your category or language filters.')}</p>
          </div>
        )}
      </div>

      <style jsx>{`
        .articles-page-container {
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
          padding: 2rem;
        }

        .articles-header {
          position: relative;
          text-align: center;
          margin-bottom: 2rem;
          padding: 2.75rem 2rem;
          border-radius: 20px;
          background:
            radial-gradient(circle at 88% -30%, rgba(255,255,255,0.18), transparent 45%),
            linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: #fff;
          box-shadow: 0 18px 40px rgba(37, 99, 235, 0.28);
          overflow: hidden;
        }
        .articles-header h1 {
          font-size: 2.4rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: #fff;
          margin: 0 0 0.5rem 0;
        }
        .articles-header p {
          font-size: 1.05rem;
          color: rgba(255,255,255,0.9);
          margin: 0 auto;
          max-width: 620px;
        }
        .content-switch { margin-top: 1.4rem; display:flex; gap:.5rem; justify-content:center; }
        .switch-btn { border:1px solid rgba(255,255,255,0.45); background: rgba(255,255,255,0.12); color:#fff; padding:.5rem 1.1rem; border-radius:999px; cursor:pointer; font-weight:600; transition: all .2s ease; }
        .switch-btn:hover { background: rgba(255,255,255,0.22); }
        .switch-btn.active { background:#fff; color:#1d4ed8; border-color:#fff; font-weight:700; }

        .filters-card {
          padding: 2rem;
          margin-bottom: 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .filter-group label {
          font-weight: 600;
          color: var(--primary-text);
          margin-bottom: 1rem;
          display: block;
        }
        .category-filters, .language-filters, .source-filters, .sort-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }
        .category-btn, .language-btn, .source-btn, .sort-btn {
          padding: 0.5rem 1.25rem;
          border: 1px solid var(--border-color);
          background-color: var(--card-bg);
          color: var(--secondary-text);
          border-radius: 2rem;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }
        .category-btn:hover, .language-btn:hover, .source-btn:hover, .sort-btn:hover {
          background-color: #f1f3f5;
          border-color: var(--secondary-text);
          color: var(--primary-text);
        }
        .category-btn.active, .language-btn.active, .source-btn.active, .sort-btn.active {
          background-color: var(--dark-bg);
          color: var(--light-text);
          border-color: var(--dark-bg);
        }

        .articles-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 1.5rem;
        }

        .article-card {
          padding: 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          height: 520px; /* Increased height for longer summaries */
        }
        .article-card.completed {
          border-left: 4px solid var(--accent-green);
        }

        .article-card-header {
          padding: 1.5rem 1.5rem 1rem 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          min-height: 80px; /* Fixed header height */
        }
        .article-card-header .header-right {
          display: flex;
          align-items: center;
        }
        .article-card-header h3 {
          font-size: 1.2rem;
          font-weight: 600;
          margin: 0;
          line-height: 1.4;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2; /* Limit to 2 lines */
          -webkit-box-orient: vertical;
        }
        .pe-points {
          background-color: var(--dark-bg);
          color: var(--light-text);
          padding: 0.25rem 0.75rem;
          border-radius: 2rem;
          font-size: 0.8rem;
          font-weight: 500;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .article-tags {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          padding: 0 1.5rem;
          min-height: 35px; /* Fixed height for tags */
          align-items: flex-start;
        }
        .tag {
          background-color: #f1f3f5;
          color: var(--secondary-text);
          padding: 0.25rem 0.75rem;
          border-radius: 2rem;
          font-size: 0.8rem;
        }

        .article-meta-info {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          padding: 0 1.5rem;
          margin-bottom: 0.5rem;
          min-height: 30px; /* Fixed height for meta info */
          align-items: flex-start;
        }

        .article-summary {
          padding: 1rem 1.5rem;
          color: var(--secondary-text);
          line-height: 1.5;
          flex-grow: 1;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 8; /* Increased from 6 to 8 lines */
          -webkit-box-orient: vertical;
          font-size: 0.95rem;
        }
        
        .article-card-footer {
          padding: 1.5rem;
          border-top: 1px solid var(--border-color);
          background-color: #f8f9fa;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: auto; /* Push to bottom */
          min-height: 70px; /* Fixed footer height */
        }
        
        .btn-read-article {
          background-color: var(--accent-green);
          color: var(--light-text);
          border: none;
          padding: 0.6rem 1.25rem;
          border-radius: 0.25rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        .btn-read-article:hover {
          background-color: var(--accent-green-hover);
        }
        
        .completed-check {
          color: var(--accent-green);
          font-weight: 600;
          font-size: 0.9rem;
        }

        .no-articles {
          grid-column: 1 / -1;
          text-align: center;
          padding: 3rem;
        }

        .loading-message {
          grid-column: 1 / -1;
          text-align: center;
          padding: 3rem;
          color: var(--secondary-text);
        }

        @media (max-width: 640px) {
          .articles-page-container {
            padding: 1rem;
          }
          .articles-header h1 {
            font-size: 1.8rem;
          }
          .articles-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          .article-card {
            height: auto;
          }
          .article-card-header {
            padding: 1rem 1rem 0.75rem 1rem;
          }
          .article-summary {
            -webkit-line-clamp: 6;
            font-size: 0.95rem;
          }
          .article-card-footer {
            flex-direction: column;
            align-items: stretch;
            gap: 0.5rem;
          }
          .btn-read-article {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export const ArticleReader = ({ 
  selectedArticle, 
  goBack, 
  completionStatuses, 
  startTest, 
  testLoading, 
  downloadCertificateFromList,
  user,
  canUserTakeTests,
  monthlyAllowance
}) => {
  const { t } = useTranslation();
  if (!selectedArticle) {
    return (
      <div className="article-reader-container">
        <p>{t('loading', 'Loading...')} {t('articles', 'Articles').toLowerCase()}</p>
      </div>
    );
  }
  
  const isCompleted = completionStatuses[selectedArticle.id]?.completed;
  const attemptsRemaining = completionStatuses[selectedArticle.id]?.attempts_remaining;
  const canTakeTests = canUserTakeTests && canUserTakeTests(user);
  // Monthly allowance gating: if user is monthly and not enough available for this article's points, disable
  const isMonthly = monthlyAllowance && monthlyAllowance.is_monthly;
  const articlePoints = parseFloat(selectedArticle.pe_points_value || 1);
  // If allowance hasn't loaded yet (available is null/undefined), don't block starting the test.
  // Only block when we definitively know the allowance is insufficient.
  const hasAllowance = !isMonthly || (
    typeof monthlyAllowance.available !== 'number'
      ? true
      : monthlyAllowance.available >= articlePoints - 1e-9
  );

  return (
    <div className="article-reader-container">
      <div className="reader-header">
        <button onClick={goBack} className="back-btn">← {t('backToLibrary', 'Back to Library')}</button>
        <div className="header-actions">
          {typeof attemptsRemaining === 'number' && (
            <span className="attempts-chip" title={t('attemptsRemaining', 'Attempts remaining')}>
              {attemptsRemaining}/3 {t('left', 'left')}
            </span>
          )}
          {isCompleted && canTakeTests && (
            <button className="btn-download" onClick={() => downloadCertificateFromList(selectedArticle.id)}>
              {t('downloadCertificate', 'Download Certificate')}
            </button>
          )}
          
          {canTakeTests && hasAllowance ? (
            <button className="btn-start-test" onClick={() => startTest(selectedArticle)} disabled={testLoading}>
              {testLoading ? t('loading', 'Loading...') : t('startTestCta', 'Start Test')}
            </button>
          ) : (
            <button className="btn-start-test-disabled" disabled>
              <span className="lock-icon">🔒</span>
              {t('lockedStartTest', 'Start Test')}
            </button>
          )}
        </div>
      </div>
      
      <div className="article-pro-header card">
        <h1>{selectedArticle.title}</h1>
        <div className="article-meta">
          <span>{selectedArticle.category}</span>
          {selectedArticle.secondary_category && selectedArticle.secondary_category !== selectedArticle.category && (
            <span>{selectedArticle.secondary_category}</span>
          )}
          {selectedArticle.subcategory && <span>{selectedArticle.subcategory}</span>}
          <span className={canTakeTests && hasAllowance ? "" : "grayed-out"}>
            {selectedArticle.pe_points_value} {parseFloat(selectedArticle.pe_points_value) === 1 ? t('pePoint', 'PE Point') : t('pePoints', 'PE Points')}
            {(!canTakeTests || !hasAllowance) && <span className="pro-badge">PRO</span>}
          </span>
          <span>{selectedArticle.difficulty_level}</span>
          <span>
            {selectedArticle.is_youtube || selectedArticle.source?.toLowerCase() === 'youtube'
              ? `${selectedArticle.estimated_read_time} ${t('minWatch', 'min watch')}`
              : `${selectedArticle.estimated_read_time} ${t('minRead', 'min read')}`}
          </span>
          {selectedArticle.source && <span>{t('source', 'Source')}: {selectedArticle.source}</span>}
          {selectedArticle.publication_date && (
            <span>{t('published', 'Published')}: {new Date(selectedArticle.publication_date).toLocaleDateString()}</span>
          )}
          {typeof attemptsRemaining === 'number' && (
            <span className="attempts-badge">{t('attemptsRemaining', 'Attempts remaining')}: {attemptsRemaining}</span>
          )}
        </div>
        {selectedArticle.summary && (
          <div className="article-summary">
            {selectedArticle.summary.split('\n').map((line, index) => (
              <span key={index}>
                {line.trim().startsWith('-') ? (
                  <span style={{ display: 'block', marginLeft: '1rem', marginBottom: '0.5rem' }}>
                    • {line.trim().substring(1).trim()}
                  </span>
                ) : (
                  <span style={{ display: line.trim() ? 'block' : 'block', marginBottom: '0.5rem' }}>
                    {line.trim() || '\u00A0'}
                  </span>
                )}
              </span>
            ))}
          </div>
        )}
        
      </div>

      {/* Article Content Section - Only show for manual text articles (no PDF and no external link) */}
      {selectedArticle.content && selectedArticle.content.trim() && !selectedArticle.has_pdf && !selectedArticle.has_external_link && (
        <div className="article-content-card card">
          <h2>{t('articleContent', 'Article Content')}</h2>
          <div className="article-text-content">
            {selectedArticle.content.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      )}

      {/* PDF Viewer Section (if PDF exists) */}
      {selectedArticle.has_pdf && selectedArticle.pdf_filename && (
        <div className="pdf-viewer-card card">
          <div className="pdf-header">
            <h2>{t('pdfDocument', 'PDF Document')}</h2>
            <a
              href={`${API_BASE_URL}/api/pdf/${selectedArticle.pdf_filename}`}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="btn-download-pdf"
            >
              {t('downloadPdf', 'Download PDF')}
            </a>
          </div>
          <iframe
            src={`${API_BASE_URL}/api/pdf/${selectedArticle.pdf_filename}`}
            width="100%"
            height="800px"
            title={`PDF: ${selectedArticle.title}`}
            className="pdf-iframe"
          >
            <p>{t('pdfUnsupported', 'Your browser does not support PDFs. Please download the PDF to view it.')}</p>
          </iframe>
        </div>
      )}

      {/* External Link or YouTube Embed */}
      {selectedArticle.has_external_link && selectedArticle.external_url && (() => {
        const url = selectedArticle.external_url;
        let ytId = '';
        try {
          const u = new URL(url);
          if (u.hostname === 'youtu.be') {
            ytId = u.pathname.replace('/', '');
          } else if (u.searchParams.get('v')) {
            ytId = u.searchParams.get('v');
          } else {
            const parts = u.pathname.split('/');
            const embedIdx = parts.findIndex(p => p === 'embed');
            if (embedIdx !== -1 && parts[embedIdx + 1]) ytId = parts[embedIdx + 1];
          }
        } catch {}

        const ExternalLinkCard = (
          <div className="external-link-card card">
          <div className="external-link-header">
            <h2>{t('externalArticle', 'External Article')}</h2>
            <p>{t('externalArticleLead', 'This article is hosted on an external website. Click the button below to read the full article.')}</p>
            </div>
            <div className="external-link-action">
              <a
                href={selectedArticle.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-external-link"
              >
                🔗 {t('readFullArticle', 'Read Full Article')}
              </a>
              <p className="external-link-note">
                {t('opensInNewTab', 'Opens in a new tab')} • {new URL(selectedArticle.external_url).hostname}
              </p>
            </div>
          </div>
        );

        if (ytId) {
          return (
            <>
              <div className="youtube-embed-card card">
                <h2>YouTube Video</h2>
                <div style={{position:'relative', paddingBottom:'56.25%', height:0, overflow:'hidden', border:'1px solid var(--border-color)', borderRadius:'8px'}}>
                  <iframe
                    title={selectedArticle.title || 'YouTube Video'}
                    src={`https://www.youtube.com/embed/${ytId}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                  />
                </div>
              </div>
              {/* Hide external link card for YouTube videos */}
            </>
          );
        }

        return ExternalLinkCard;
      })()}

      {/* Article Feedback Form */}
      {user && (
        <ArticleFeedbackForm 
          article={selectedArticle}
          user={user}
          onFeedbackSubmitted={(feedback) => {
            console.log('Feedback submitted:', feedback);
            // You can add additional logic here if needed
          }}
        />
      )}
      
      <style jsx>{`
        .article-reader-container {
          width: 100%;
          max-width: 1000px;
          margin: 0 auto;
          padding: 2rem;
        }

        .reader-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }
        
        .back-btn {
          background: none;
          border: 1px solid var(--border-color);
          padding: 0.6rem 1.25rem;
          border-radius: 0.25rem;
          font-weight: 600;
          color: var(--secondary-text);
          cursor: pointer;
          transition: all 0.2s;
        }
        .back-btn:hover {
          background-color: #f1f3f5;
          border-color: #adb5bd;
          color: var(--primary-text);
        }

        .header-actions {
          display: flex;
          gap: 1rem;
        }

        .btn-start-test, .btn-download {
          color: var(--light-text);
          border: none;
          padding: 0.6rem 1.25rem;
          border-radius: 0.25rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        .btn-start-test {
          background-color: var(--accent-green);
        }
        .btn-start-test:hover {
          background-color: var(--accent-green-hover);
        }
        .btn-download {
          background-color: var(--dark-bg);
        }
        .btn-download:hover {
          background-color: #495057;
        }

        .article-pro-header {
          text-align: center;
          padding: 2rem;
          margin-bottom: 2rem;
        }
        .article-pro-header h1 {
          font-size: 2.2rem;
          font-weight: 600;
          color: var(--primary-text);
          margin: 0 0 1rem 0;
          line-height: 1.3;
        }
        .article-meta {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }
        .article-meta span {
          background-color: #f1f3f5;
          color: var(--secondary-text);
          padding: 0.25rem 0.75rem;
          border-radius: 2rem;
          font-size: 0.9rem;
        }
        .article-summary {
          font-size: 1.1rem;
          color: var(--secondary-text);
          line-height: 1.6;
          max-width: 800px;
          margin: 0 auto;
        }

        .article-content-card {
          margin-bottom: 2rem;
        }
        .article-content-card h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--primary-text);
          margin: 0 0 1.5rem 0;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid var(--border-color);
        }
        .article-text-content {
          font-size: 1.1rem;
          line-height: 1.8;
          color: var(--primary-text);
          max-width: none;
        }
        .article-text-content p {
          margin-bottom: 1.5rem;
        }
        .article-text-content h1, .article-text-content h2, .article-text-content h3 {
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: var(--dark-bg);
        }
        .article-text-content ul, .article-text-content ol {
          margin-bottom: 1.5rem;
          padding-left: 2rem;
        }
        .article-text-content li {
          margin-bottom: 0.5rem;
        }

        .pdf-viewer-card {
          padding: 1rem;
        }

        .pdf-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid var(--border-color);
        }

        .pdf-header h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--primary-text);
          margin: 0;
        }

        .btn-download-pdf {
          background-color: var(--dark-bg);
          color: var(--light-text);
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 0.25rem;
          font-weight: 500;
          cursor: pointer;
          text-decoration: none;
          transition: background-color 0.2s;
        }

        .btn-download-pdf:hover {
          background-color: #495057;
        }

        .pdf-iframe {
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius);
        }
        @media (max-width: 640px) {
          .pdf-iframe {
            height: 60svh;
          }
        }

        .external-link-card {
          padding: 1rem;
        }

        .external-link-header {
          text-align: center;
          margin-bottom: 1rem;
        }

        .external-link-header h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--primary-text);
          margin: 0 0 0.5rem 0;
        }

        .external-link-action {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
        }

        .btn-external-link {
          background-color: var(--accent-green);
          color: var(--light-text);
          border: none;
          padding: 0.6rem 1.25rem;
          border-radius: 0.25rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-external-link:hover {
          background-color: var(--accent-green-hover);
        }

        .external-link-note {
          color: var(--secondary-text);
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .btn-start-test-disabled {
          background-color: #a3d9a5;
          color: var(--light-text);
          border: none;
          padding: 0.6rem 1.25rem;
          border-radius: 0.25rem;
          font-weight: 500;
          cursor: not-allowed;
          opacity: 0.7;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: none;
        }

        .grayed-out {
          color: var(--secondary-text);
          opacity: 0.7;
        }

        .pro-badge {
          background-color: var(--accent-green);
          color: var(--light-text);
          padding: 0.25rem 0.75rem;
          border-radius: 2rem;
          font-size: 0.8rem;
          font-weight: 500;
          margin-left: 0.5rem;
        }

        .attempts-badge {
          background-color: #f1f3f5;
          color: var(--secondary-text);
          padding: 0.25rem 0.6rem;
          border-radius: 999px;
          font-size: 0.85rem;
        }

        .attempts-chip {
          display: inline-flex;
          align-items: center;
          height: 28px;
          padding: 0 8px;
          margin-right: .5rem;
          border-radius: 999px;
          border: 1px solid var(--border-color);
          color: var(--secondary-text);
          font-size: 0.8rem;
          background: #fff;
        }

        @media (max-width: 640px) {
          .article-reader-container {
            padding: 1rem;
          }
          .reader-header {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }
          .header-actions {
            justify-content: center;
            flex-direction: column;
            gap: 0.5rem;
          }
          .header-actions .btn-start-test,
          .header-actions .btn-download,
          .header-actions .btn-start-test-disabled {
            width: 100%;
          }
          .article-pro-header h1 {
            font-size: 1.8rem;
          }
          .article-meta {
            gap: 0.75rem;
          }
          .article-text-content {
            font-size: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export const TestInterface = ({ 
  selectedArticle, 
  testQuestions, 
  currentQuestionIndex, 
  setCurrentQuestionIndex, 
  selectedAnswers, 
  setSelectedAnswers, 
  testResults, 
  setTestResults, 
  goBack, 
  downloadCertificate, 
  fetchCompletionData, 
  user, 
  API_BASE_URL 
}) => {
  const { t } = useTranslation();
  // Generate unique key for localStorage based on user and article
  const storageKey = selectedArticle && user ? `test_progress_${user.id}_${selectedArticle.id}` : null;

  // Load saved answers when component mounts
  React.useEffect(() => {
    if (!storageKey) return;
    
    try {
      const savedProgress = localStorage.getItem(storageKey);
      if (savedProgress) {
        const { answers, questionIndex } = JSON.parse(savedProgress);
        setSelectedAnswers(answers || {});
        setCurrentQuestionIndex(questionIndex || 0);
      }
    } catch (error) {
      console.error('Error loading saved test progress:', error);
    }
  }, [storageKey, setSelectedAnswers, setCurrentQuestionIndex]);

  // Save progress whenever answers or question index changes
  React.useEffect(() => {
    if (!storageKey) return;
    
    if (Object.keys(selectedAnswers).length > 0 || currentQuestionIndex > 0) {
      try {
        const progressData = {
          answers: selectedAnswers,
          questionIndex: currentQuestionIndex,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem(storageKey, JSON.stringify(progressData));
      } catch (error) {
        console.error('Error saving test progress:', error);
      }
    }
  }, [selectedAnswers, currentQuestionIndex, storageKey]);

  if (!selectedArticle || testQuestions.length === 0) {
    return (
      <div className="test-loading">
        <p>{t('loading', 'Loading...')}</p>
      </div>
    );
  }

  const currentQuestion = testQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === testQuestions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  const handleAnswerSelect = (questionId, answer) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < testQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmitTest = async () => {
    try {
      const response = await fetch(`${API_URL}/users/${user.id}/articles/${selectedArticle.id}/submit-test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answers: selectedAnswers })
      });

      if (response.ok) {
        const data = await response.json();
        setTestResults(data);
        // Clear saved progress after successful submission
        if (storageKey) {
          localStorage.removeItem(storageKey);
        }
        // Refresh completion data
        fetchCompletionData();
      } else {
        const errorData = await response.json();
        alert(`Error submitting test: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      alert('Error submitting test');
    }
  };

  const handleGoBack = () => {
    // Show confirmation if user has answered questions
    if (Object.keys(selectedAnswers).length > 0) {
      const confirmLeave = window.confirm(
        'Your test progress has been saved automatically. You can return to continue this test later. Do you want to go back to the article?'
      );
      if (confirmLeave) {
        goBack();
      }
    } else {
      goBack();
    }
  };

  if (testResults) {
    return (
      <div className="test-results-container">
        <div className="card results-card">
          <div className="results-card-header">
            <h2>{t('testResults', 'Test Results')}</h2>
            <p>{t('forLabel', 'For')}: {selectedArticle.title}</p>
          </div>

          <div className="score-summary">
            <div className={`score-badge ${testResults.passed ? 'passed' : 'failed'}`}>
              {Number.isFinite(testResults.percentage) ? Math.round(testResults.percentage) : 0}%
            </div>
            <div className="score-details">
              <p>{t('youAnswered', 'You answered')} {testResults.score} {t('of', 'of')} {testResults.total} {t('questionsCorrectly', 'questions correctly.')}</p>
              <p className={`score-verdict ${testResults.passed ? 'passed' : 'failed'}`}>
                {testResults.passed ? t('passedCongrats', 'Congratulations, you passed!') : t('failedTryAgain', 'A score of 80% is required to pass. Please try again.')}
              </p>
              {typeof testResults.attempts_remaining === 'number' && (
                <p style={{ marginTop: '.5rem', fontWeight: 600 }}>
                  {t('attemptsRemaining', 'Attempts remaining')}: {testResults.attempts_remaining} / 3
                </p>
              )}
            </div>
          </div>
          
          <div className="results-card-footer">
            {testResults.passed ? (
              <>
                <button className="btn-secondary-action" onClick={handleGoBack}>
                  {t('backToLibrary', 'Back to Library')}
                </button>
                <button className="btn-primary-action" onClick={downloadCertificate}>
                  {t('downloadCertificate', 'Download Certificate')}
                </button>
              </>
            ) : (
              <>
                <button className="btn-secondary-action" onClick={handleGoBack}>
                  {t('reviewArticle', 'Review Article')}
                </button>
                <button
                  className="btn-primary-action"
                  onClick={() => {
                    setTestResults(null);
                    setCurrentQuestionIndex(0);
                    setSelectedAnswers({});
                  }}
                >
                  {t('retakeTest', 'Retake Test')}
                </button>
              </>
            )}
          </div>
        </div>
        {testResults && testResults.passed && Array.isArray(testResults.results) && (() => {
          const incorrect = (testResults.results || []).filter(r => r && r.is_correct === false);
          if (incorrect.length === 0) return null;
          return (
            <div className="card" style={{ margin: '1.5rem 0', padding: '1rem', borderTop: '1px solid var(--border-color)' }}>
              <h3 style={{ marginTop: 0 }}>{t('reviewIncorrectQuestions', 'Review incorrect questions')}</h3>
              {incorrect.map((r, idx) => {
                const question = (testQuestions || []).find(q => q.id === r.question_id);
                const resolveText = (q, letter) => {
                  if (!q || !letter) return '';
                  const key = String(letter).toLowerCase();
                  const map = { a: q.option_a, b: q.option_b, c: q.option_c, d: q.option_d };
                  return map[key] || '';
                };
                const userText = resolveText(question, r.user_answer);
                const correctText = resolveText(question, r.correct_answer);
                const questionIndex = (testQuestions || []).findIndex(q => q.id === r.question_id);
                const numberedLabel = (questionIndex >= 0 ? questionIndex + 1 : idx + 1);
                const totalCount = (testQuestions || []).length || incorrect.length;
                return (
                  <div key={idx} style={{ marginTop: idx === 0 ? 0 : '1rem', paddingTop: idx === 0 ? 0 : '1rem', borderTop: idx === 0 ? 'none' : '1px solid #eee' }}>
                    <div style={{ fontWeight: 600, marginBottom: '.25rem' }}>{t('question', 'Question')} {numberedLabel} {t('of', 'of')} {totalCount}</div>
                    <div style={{ marginBottom: '.5rem' }}>{r.question || (question && question.question) || `Question ${idx + 1}`}</div>
                    <div style={{ marginBottom: '.25rem' }}>
                      <span style={{ fontWeight: 600, color: '#7f1d1d' }}>{t('yourAnswer', 'Your answer:')}</span>
                      <span style={{ color: '#7f1d1d' }}> {r.user_answer}{userText ? ` — ${userText}` : ''}</span>
                    </div>
                    <div style={{ marginBottom: '.25rem' }}>
                      <span style={{ fontWeight: 600, color: '#065f46' }}>{t('correctAnswer', 'Correct answer:')}</span>
                      <span style={{ color: '#065f46' }}> {r.correct_answer}{correctText ? ` — ${correctText}` : ''}</span>
                    </div>
                    {r.explanation && (
                      <div style={{ color: '#374151' }}>
                        <span style={{ fontWeight: 600 }}>{t('explanation', 'Explanation:')}</span>
                        <span> {r.explanation}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
        <style jsx>{`
          .test-results-container {
            max-width: 800px;
            margin: 2rem auto;
            padding: 2rem;
          }
          .results-card {
            padding: 0;
            overflow: hidden;
            border: 1px solid var(--border-color);
          }
          .results-card-header {
            background-color: var(--dark-bg);
            color: var(--light-text);
            padding: 2rem;
            text-align: center;
          }
          .results-card-header h2 {
            margin: 0 0 0.5rem 0;
            font-size: 2rem;
          }
          .results-card-header p {
            margin: 0;
            opacity: 0.9;
          }
          .score-summary {
            padding: 2.5rem;
            display: flex;
            align-items: center;
            gap: 2rem;
          }
          .score-badge {
            font-size: 2.5rem;
            font-weight: 700;
            padding: 1.5rem;
            min-width: 120px;
            text-align: center;
            border-radius: var(--border-radius);
          }
          .score-badge.passed {
            background-color: #e8f5e8;
            color: var(--accent-green);
          }
          .score-badge.failed {
            background-color: #fff0f0;
            color: #d93a3a;
          }
          .score-details p {
            margin: 0 0 0.5rem 0;
            color: var(--primary-text);
          }
          .score-verdict {
            font-weight: 600;
          }
          .score-verdict.passed {
            color: var(--accent-green);
          }
          .score-verdict.failed {
            color: #d93a3a;
          }
          .results-card-footer {
            background-color: #f8f9fa;
            padding: 1.5rem;
            border-top: 1px solid var(--border-color);
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
          }
          .btn-primary-action, .btn-secondary-action {
            padding: 0.6rem 1.25rem;
            border-radius: 0.25rem;
            font-weight: 500;
            cursor: pointer;
            border: 1px solid transparent;
            transition: all 0.2s;
          }
          .btn-primary-action {
            background-color: var(--accent-green);
            color: var(--light-text);
          }
          .btn-primary-action:hover {
            background-color: var(--accent-green-hover);
          }
          .btn-secondary-action {
            background-color: var(--card-bg);
            color: var(--primary-text);
            border: 1px solid var(--border-color);
          }
          .btn-secondary-action:hover {
            background-color: #f1f3f5;
            border-color: var(--secondary-text);
          }

          @media (max-width: 640px) {
            .test-results-container {
              padding: 1rem;
            }
            .score-summary {
              padding: 1.5rem;
              flex-direction: column;
              align-items: stretch;
            }
            .results-card-footer {
              justify-content: stretch;
              flex-direction: column;
            }
            .btn-primary-action, .btn-secondary-action {
              width: 100%;
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="test-interface-container">
      <div className="test-header card">
        <h1>Certification Test</h1>
        <p>{selectedArticle.title}</p>
      </div>

      <div className="test-body">
        <div className="question-card card">
          <div className="question-progress">
            Question {currentQuestionIndex + 1} of {testQuestions.length}
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${((currentQuestionIndex + 1) / testQuestions.length) * 100}%` }}
              />
            </div>
          </div>
          
          <h3 className="question-text">{currentQuestion.question}</h3>
          
          <div className="answer-options">
            {['A', 'B', 'C', 'D'].map(option => (
              <label key={option} className={`answer-option ${selectedAnswers[currentQuestion.id] === option ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name={`question-${currentQuestion.id}`}
                  value={option}
                  checked={selectedAnswers[currentQuestion.id] === option}
                  onChange={() => handleAnswerSelect(currentQuestion.id, option)}
                />
                <span className="option-letter">{option}</span>
                <span className="option-text">{currentQuestion[`option_${option.toLowerCase()}`]}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="test-navigation card">
          <button className="btn-navigation" onClick={handleGoBack}>
            Exit Test
          </button>
          
          <div className="nav-buttons">
            <button 
              className="btn-navigation"
              onClick={handlePrevious}
              disabled={isFirstQuestion}
            >
              Previous
            </button>
            {isLastQuestion ? (
              <button 
                className="btn-submit"
                onClick={handleSubmitTest}
                disabled={Object.keys(selectedAnswers).length !== testQuestions.length}
              >
                Submit Test
              </button>
            ) : (
              <button 
                className="btn-navigation"
                onClick={handleNext}
                disabled={!selectedAnswers[currentQuestion.id]}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .test-interface-container {
          max-width: 900px;
          margin: 2rem auto;
          padding: 2rem;
        }

        .test-loading {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 50vh;
          font-size: 1rem;
          color: var(--secondary-text);
        }

        .test-header {
          text-align: center;
          padding: 2rem;
          margin-bottom: 2rem;
        }
        .test-header h1 {
          font-size: 2.2rem;
          font-weight: 600;
          color: var(--primary-text);
          margin: 0 0 0.5rem 0;
        }
        .test-header p {
          color: var(--secondary-text);
          font-size: 1.1rem;
          margin: 0;
        }

        .question-card {
          padding: 2.5rem;
          margin-bottom: 1.5rem;
        }

        .question-progress {
          color: var(--secondary-text);
          font-size: 0.9rem;
          margin-bottom: 2rem;
        }
        .progress-bar {
          width: 100%;
          height: 8px;
          background: var(--border-color);
          border-radius: 4px;
          overflow: hidden;
          margin-top: 0.5rem;
        }
        .progress-fill {
          height: 100%;
          background: var(--accent-green);
          transition: width 0.3s ease;
        }

        .question-text {
          font-size: 1.3rem;
          font-weight: 600;
          margin: 0 0 2rem 0;
          line-height: 1.5;
        }

        .answer-options {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .answer-option {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius);
          cursor: pointer;
          transition: all 0.2s;
        }
        .answer-option:hover {
          border-color: var(--accent-green);
          background-color: #f8f9fa;
        }
        .answer-option.selected {
          border-color: var(--accent-green);
          background-color: #e8f5e8;
          font-weight: 500;
        }

        .answer-option input[type="radio"] {
          display: none;
        }
        .option-letter {
          background-color: var(--border-color);
          color: var(--primary-text);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          flex-shrink: 0;
          transition: all 0.2s;
        }
        .answer-option.selected .option-letter {
          background-color: var(--accent-green);
          color: var(--light-text);
        }
        .option-text {
          color: var(--primary-text);
        }

        .test-navigation {
          padding: 1rem 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .btn-navigation, .btn-submit {
          padding: 0.6rem 1.25rem;
          border-radius: 0.25rem;
          font-weight: 500;
          cursor: pointer;
          border: 1px solid transparent;
          transition: all 0.2s;
        }
        .btn-navigation {
            background-color: var(--card-bg);
            color: var(--primary-text);
            border: 1px solid var(--border-color);
        }
        .btn-navigation:hover:not(:disabled) {
          background-color: #f1f3f5;
          border-color: var(--secondary-text);
        }
        .btn-navigation:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .nav-buttons {
          display: flex;
          gap: 1rem;
        }
        .btn-submit {
          background-color: var(--accent-green);
          color: var(--light-text);
        }
        .btn-submit:hover:not(:disabled) {
          background-color: var(--accent-green-hover);
        }
        .btn-submit:disabled {
          background-color: #a3d9a5;
          cursor: not-allowed;
        }

        @media (max-width: 640px) {
          .test-interface-container {
            padding: 1rem;
          }
          .test-header {
            padding: 1.5rem;
          }
          .question-card {
            padding: 1.25rem;
          }
          .question-text {
            font-size: 1.1rem;
          }
          .test-navigation {
            flex-direction: column;
            gap: 0.75rem;
            align-items: stretch;
          }
          .nav-buttons {
            flex-direction: column;
          }
          .btn-navigation,
          .btn-submit {
            width: 100%;
            min-height: 44px;
          }
        }
      `}</style>
    </div>
  );
};

export const NewsUpdatesPage = ({ newsItems, goBack }) => {
  const { t } = useTranslation();
  // Group news by type for better organization
  const groupedNews = newsItems.reduce((acc, item) => {
    if (!acc[item.type]) {
      acc[item.type] = [];
    }
    acc[item.type].push(item);
    return acc;
  }, {});

  const getTypeInfo = (type) => {
    switch (type) {
      case 'news':
        return { icon: '📰', label: 'News', color: '#6c757d' };
      case 'feature':
        return { icon: '🔵', label: 'Feature', color: '#007bff' };
      case 'announcement':
        return { icon: '🟢', label: 'Announcement', color: '#28a745' };
      case 'update':
        return { icon: '🟡', label: 'Update', color: '#ffc107' };
      default:
        return { icon: '📰', label: 'News', color: '#6c757d' };
    }
  };

  return (
    <div className="news-updates-container">
      <div className="news-header">
        <h1>{t('dashboard.latestUpdates', 'Latest Updates')}</h1>
        <p>{t('news.stayUpToDate', 'Stay up to date with the latest news, features, and announcements.')}</p>
      </div>

      <div className="news-content">
        {Object.keys(groupedNews).length > 0 ? (
          Object.entries(groupedNews).map(([type, items]) => {
            const typeInfo = getTypeInfo(type);
            return (
              <div key={type} className="news-section card">
                <div className="section-header">
                  <span className="section-icon" style={{ color: typeInfo.color }}>
                    {typeInfo.icon}
                  </span>
                  <h2>{typeInfo.label}</h2>
                  <span className="item-count">{items.length} {items.length !== 1 ? t('news.items', 'items') : t('news.item', 'item')}</span>
                </div>
                <div className="news-items">
                  {items.map(item => (
                    <div key={item.id} className="news-item">
                      <div className="news-item-header">
                        <h3>{item.title}</h3>
                        <span className="news-date">
                          {new Date(item.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="news-content-text">
                        {item.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-news card">
            <h3>{t('news.noUpdates', 'No updates available')}</h3>
            <p>{t('news.checkBack', 'Check back later for the latest news and updates.')}</p>
          </div>
        )}
      </div>

      <style>
        {`
        .news-updates-container {
          width: 100%;
          max-width: 1000px;
          margin: 0 auto;
          padding: 2rem;
        }

        .news-header {
          position: relative;
          text-align: center;
          margin-bottom: 2.5rem;
          padding: 2.75rem 2rem;
          border-radius: 20px;
          background:
            radial-gradient(circle at 88% -30%, rgba(255,255,255,0.18), transparent 45%),
            linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: #fff;
          box-shadow: 0 18px 40px rgba(37, 99, 235, 0.28);
          overflow: hidden;
        }
        .news-header h1 {
          font-size: 2.4rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: #fff;
          margin: 0 0 0.5rem 0;
        }
        .news-header p {
          font-size: 1.05rem;
          color: rgba(255,255,255,0.9);
          margin: 0 auto;
          max-width: 620px;
        }

        .news-content {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .news-section {
          padding: 0;
          overflow: hidden;
        }

        .section-header {
          background-color: #f8f9fa;
          padding: 1.5rem;
          border-bottom: 1px solid var(--border-color);
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .section-icon {
          font-size: 1.5rem;
        }
        .section-header h2 {
          font-size: 1.3rem;
          font-weight: 600;
          margin: 0;
          flex-grow: 1;
        }
        .item-count {
          background-color: var(--border-color);
          color: var(--secondary-text);
          padding: 0.25rem 0.75rem;
          border-radius: 2rem;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .news-items {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .news-item {
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #f1f3f5;
        }
        .news-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .news-item-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .news-item-header h3 {
          font-size: 1.2rem;
          font-weight: 600;
          margin: 0;
          color: var(--primary-text);
          line-height: 1.4;
        }
        .news-date {
          color: var(--secondary-text);
          font-size: 0.9rem;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .news-content-text {
          color: var(--primary-text);
          line-height: 1.6;
          font-size: 1rem;
        }

        .no-news {
          text-align: center;
          padding: 3rem;
        }
        .no-news h3 {
          color: var(--primary-text);
          margin-bottom: 0.5rem;
        }
        .no-news p {
          color: var(--secondary-text);
        }

        @media (max-width: 768px) {
          .news-updates-container {
            padding: 1rem;
          }
          .news-header h1 {
            font-size: 2rem;
          }
          .news-item-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
          .section-header {
            padding: 1rem;
          }
          .news-items {
            padding: 1rem;
          }
        }
        `}
      </style>
    </div>
  );
}; 